import {advancedFormControl, advancedFormGroup, FormControlTypeEnum,} from '@magieno/common';
import {translationBaseKey} from '../../../../../decorators/translation-base-key.decorator';
import {CalculationRunOptions} from '../../../calculation-run.options';
import {CalculationKeynameEnum} from '../../../../../enums/calculations/calculation-keyname.enum';
import {IsInt, IsNotEmpty, IsNumber, Max, Min, ValidateNested} from '@pristine-ts/class-validator';
import {FieldTypeEnum} from '../../../../../enums/field-type.enum';
import {ErrorMessageBuilder} from '../../../../../builders/error-message.builder';
import {CorporateIncomeTaxQuebecInCorporateIncomeTaxOptions,} from './corporate-income-tax-quebec-in-corporate-income-tax.options';
import {CorporateIncomeTaxFederalInCorporateIncomeTaxOptions,} from './corporate-income-tax-federal-in-corporate-income-tax.options';
import {CorporateIncomeTaxCommonOptions} from '../common/corporate-income-tax-common.options';
import {clonePropertyMetadata} from '@pristine-ts/metadata';
import {CorporateIncomeTaxFederalOptions} from '../federal/corporate-income-tax-federal.options';

/**
 * The Corporate Income Tax calculation is the calculation that calculates the federal and provincial income tax.
 *
 * It must extend the `CorporateIncomeTaxCommonOptions` since it needs to have all the fields required to call Federal and
 * Quebec. Since all the fields that are common in Federal and Quebec are in CorporateIncomeTaxCommonOptions, we can
 * directly extend it.
 *
 * If some common fields inside `CorporateIncomeTaxCommonOptions` were to become calculated as part of CorporateIncomeTax,
 * we should instead implement CorporateIncomeTaxCommonOptions and `Omit`, since they would not be required in the form
 * to execute this calculation.
 *
 * We also have two properties, one for `CorporateIncomeTaxFederalInCorporateIncomeTaxOptions` and
 * one for `CorporateIncomeTaxQuebecInCorporateIncomeTaxOptions` that contain only the properties that must be filled out.
 *
 * The other properties required by the `CorporateIncomeTaxFederalOptions` but not defined in
 * `CorporateIncomeTaxFederalInCorporateIncomeTaxOptions` will be filled out by this calculation.
 *
 * The other properties required by the `CorporateIncomeTaxQuebecOptions` but not defined in
 * `CorporateIncomeTaxQuebecInCorporateIncomeTaxOptions` will be filled out by this calculation.
 *
 * The idea is that this calculation either calculates things from one calculation (federal) and passes it to the other
 * (quebec), or, it makes assumptions that cannot be modified. If you don't want these assumptions, you can execute
 * the `CorporateIncomeTaxFederal` or `CorporateIncomeTaxQuebec` calculations directly.
 */
@translationBaseKey('income-tax-corporation-options')
export class CorporateIncomeTaxOptions extends CorporateIncomeTaxCommonOptions implements CalculationRunOptions {

  @advancedFormControl({type: FormControlTypeEnum.Hidden})
  calculationKeyname: CalculationKeynameEnum = CalculationKeynameEnum.CorporateTax;

  @advancedFormGroup({
    classType: CorporateIncomeTaxQuebecInCorporateIncomeTaxOptions,
  })
  @ValidateNested()
  incomeTaxCorporationQuebec: CorporateIncomeTaxQuebecInCorporateIncomeTaxOptions;

  @advancedFormGroup({
    classType: CorporateIncomeTaxFederalInCorporateIncomeTaxOptions,
  })
  @ValidateNested()
  incomeTaxCorporationFederal: CorporateIncomeTaxFederalInCorporateIncomeTaxOptions;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Dans le cas ou le salaire simulé engendre une perte pour la société, quel est le taux d\'imposition de la société quant au report de cette perte?',
        en: 'If the simulated salary causes a loss for the corporation, what it the tax rate for the loss.',
      },
      tooltip: {
        fr: 'Le logiciel a besoin du taux d’imposition lors d’un report de perte lorsque le salaire simulé par le logiciel plus les cotisations de l’employeur équivalente engendre une perte dans l’exercice qui est reporté à un autre exercice. Le logiciel pourra calculer l’économie d’impôt relatif au report de perte.',
        en: 'The software needs the tax rate when carrying forward a loss when the salary simulated by the software plus the equivalent employer contributions results in a loss in the fiscal year that is carried forward to another fiscal year. The software will be able to calculate the tax savings relating to loss carryover.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsNumber(undefined, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @Min(0, { buildErrorMessage: ErrorMessageBuilder.build })
  @Max(100, { buildErrorMessage: ErrorMessageBuilder.build })
    // todo should this be optional ? Dans le cas ou le salaire simulé
  lossTaxRate: number;

  // todo verify this label
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Excedent de gain ou de perte imposable',
        en: 'Taxable gain or loss excess',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  taxableGainLossExcedent: number;

  @clonePropertyMetadata<CorporateIncomeTaxFederalOptions>(CorporateIncomeTaxFederalOptions, 'interestIncome')
  interestIncome: number;
}

